import { createTheme } from '@mui/material/styles';

const getTheme = (mode) => createTheme({
  palette: {
    mode,
    primary: {
      main: '#03C407',
      light: '#2ACD08',
      dark: '#029406',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#FF9600',
      light: '#FFB84D',
      dark: '#CC7900',
      contrastText: '#000000',
    },
    error: {
      main: '#FF5252',
      light: '#FF8A80',
      dark: '#D32F2F',
    },
    warning: {
      main: '#FF9600',
      light: '#FFB84D',
      dark: '#CC7900',
    },
    info: {
      main: '#64B5F6',
      light: '#90CAF9',
      dark: '#1976D2',
    },
    success: {
      main: '#03C407',
      light: '#2ACD08',
      dark: '#029406',
    },
    background: {
      default: mode === 'dark' ? '#183239' : '#F5F5F5',
      paper: mode === 'dark' ? '#1E3A4C' : '#FFFFFF',
    },
    text: {
      primary: mode === 'dark' ? '#E0E0E0' : '#1A1A1A',
      secondary: mode === 'dark' ? '#A0A0A0' : '#666666',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 500,
      fontSize: '2.5rem',
    },
    h2: {
      fontWeight: 500,
      fontSize: '2rem',
    },
    h3: {
      fontWeight: 500,
      fontSize: '1.75rem',
    },
    h4: {
      fontWeight: 500,
      fontSize: '1.5rem',
    },
    h5: {
      fontWeight: 500,
      fontSize: '1.25rem',
    },
    h6: {
      fontWeight: 500,
      fontSize: '1rem',
    },
    subtitle1: {
      fontSize: '1rem',
    },
    subtitle2: {
      fontSize: '0.875rem',
    },
    body1: {
      fontSize: '1rem',
    },
    body2: {
      fontSize: '0.875rem',
    },
    button: {
      textTransform: 'none',
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: mode === 'dark' ? '#1E3A4C' : '#FFFFFF',
          color: mode === 'dark' ? '#E0E0E0' : '#1A1A1A',
          border: `1px solid ${mode === 'dark' ? '#2E5060' : '#ddd'}`,
          fontSize: '0.875rem',
          boxShadow: '0px 2px 8px rgba(0,0,0,0.15)',
        },
        arrow: {
          color: mode === 'dark' ? '#1E3A4C' : '#FFFFFF',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.08)',
          borderRadius: 12,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(0, 0, 0, 0.23)',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#FF9600',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#FF9600 !important',
            borderWidth: '2px !important',
          },
        },
      },
      defaultProps: {
        notched: false,
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          '&.Mui-focused': {
            color: '#FF9600',
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#FF9600 !important',
            borderWidth: '2px !important',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '8px 16px',
          fontWeight: 600,
          textTransform: 'none',
          '&:hover': {
            backgroundColor: mode === 'dark' ? 'rgba(3, 196, 7, 0.15)' : 'rgba(3, 196, 7, 0.1)',
          },
        },
        contained: {
          backgroundColor: '#03C407',
          color: '#FFFFFF',
          '&:hover': {
            backgroundColor: '#029406',
          },
          '&.Mui-disabled': {
            backgroundColor: mode === 'dark' ? 'rgba(3, 196, 7, 0.3)' : 'rgba(3, 196, 7, 0.3)',
            color: mode === 'dark' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.3)',
          },
        },
        outlined: {
          borderColor: '#03C407',
          color: '#03C407',
          '&:hover': {
            borderColor: '#029406',
            backgroundColor: mode === 'dark' ? 'rgba(3, 196, 7, 0.15)' : 'rgba(3, 196, 7, 0.1)',
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: mode === 'dark' ? '#E0E0E0' : '#1A1A1A',
          '&:hover': {
            backgroundColor: mode === 'dark' ? 'rgba(3, 196, 7, 0.12)' : 'rgba(3, 196, 7, 0.08)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          color: mode === 'dark' ? '#A0A0A0' : '#666666',
          '&.Mui-selected': {
            color: '#03C407',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 16,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: mode === 'dark' ? 'rgba(3, 196, 7, 0.12)' : 'rgba(3, 196, 7, 0.08)',
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(3, 196, 7, 0.16)',
            '&:hover': {
              backgroundColor: 'rgba(3, 196, 7, 0.20)',
            },
          },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: mode === 'dark' ? 'rgba(3, 196, 7, 0.12)' : 'rgba(3, 196, 7, 0.08)',
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(3, 196, 7, 0.16)',
            '&:hover': {
              backgroundColor: 'rgba(3, 196, 7, 0.20)',
            },
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:nth-of-type(odd)': {
            backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.02)',
          },
          '&:hover': {
            backgroundColor: mode === 'dark' ? 'rgba(3, 196, 7, 0.08)' : 'rgba(3, 196, 7, 0.04)',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`,
        },
        head: {
          fontWeight: 600,
          backgroundColor: mode === 'dark' ? '#153444' : '#F0F0F0',
        },
      },
    },
  },
});

export const darkTheme = getTheme('dark');
export const lightTheme = getTheme('light');
export { getTheme };
export default darkTheme;
